import { useMemo } from 'react';

import { useCurrentCompany } from '@bonusly/who-am-i';

import { ActionModule } from '../types';

interface UseBaseModulesOptions {
	includeEmoji?: boolean;
	includeHashtag?: boolean;
	includeLink?: boolean;
	includeMention?: boolean;
	includeAmount?: boolean;
	includePrivacy?: boolean;
	includeGif?: boolean;
	includeVideoRecording?: boolean;
	includeAudioRecording?: boolean;
	includeAttachment?: boolean;
	includeRecipient?: boolean;
	includeSuggestionHighlight?: boolean;
	includeDoodle?: boolean;
}

// This hook should be used to define and enforce basic conditions around which modules
// are available to the user.
//
// This should include base-level invariants that are true or false regardless of the context
// in which it is being used. e.g. These should be the ones that trump anything context specific.
//   "the company can turn off video uploads"
//   "the company allows privacy settings to be changed"
//
// It should not be used to define invariants around which modules are available within a specific
// usage context e.g.
//   "amount is available when giving peer to peer with non-zero bonuses and the user has a non-zero giving balance"
//
// For enforcing invariants in context-specific situations you can create a new hook that
// consumes this hook and passes in the context-specific options. For example:
// useBaseModules({
//   includeAmount: user.can_give_non_zero && !(company.zero_point_bonuses && user.giving_balance === 0)
// })
//
// These options will be && together with any base invariants enforced herein to determine the
// final usability of a module.
//
// A context-specific hook can also add *new* modules that are not available in the base set by
// first calling this hook and adding new modules to the returned set. All of these modules would
// then be available to be checked using the useIsEnabled('module-key') hook.
export const useBaseModules = ({
	includeEmoji = false,
	includeHashtag = false,
	includeLink = false,
	includeMention = false,
	includeAmount = false,
	includePrivacy = false,
	includeGif = false,
	includeVideoRecording = false,
	includeAudioRecording = false,
	includeAttachment = false,
	includeRecipient = false,
	includeSuggestionHighlight = false,
	includeDoodle = false,
}: UseBaseModulesOptions = {}) => {
	const company = useCurrentCompany();
	const isImageEnabled = !company.disable_image_uploads;

	const canUploadVideo = !company.disable_video_uploads;
	const canRecordVideo = !company.disable_video_recordings;
	const canRecordAudio = !company.disable_video_recordings;

	return useMemo(() => {
		const mods: ActionModule[] = [];

		if (includeEmoji) mods.push('emoji');
		if (includeHashtag) mods.push('hashtag');
		if (includeLink) mods.push('link');
		if (includeMention) mods.push('mention');
		if (includeAmount) mods.push('amount');

		if (includePrivacy && company.bonus_privacy) {
			mods.push('privacy');
		}

		if (includeGif && !company.disable_gifs) {
			mods.push('gif');
		}

		if (includeVideoRecording && canRecordVideo) {
			mods.push('video-recording');
		}

		if (includeAudioRecording && canRecordAudio) {
			mods.push('audio-recording');
		}

		if (includeAttachment) {
			if (isImageEnabled && canUploadVideo) {
				mods.push('attachment');
			} else if (canUploadVideo) {
				mods.push('attachment');
			} else if (isImageEnabled) {
				mods.push('image');
			}
		}

		if (includeRecipient) {
			mods.push('recipient');
		}

		if (includeSuggestionHighlight) {
			mods.push('suggestion-highlight');
		}

		if (includeDoodle && isImageEnabled) {
			mods.push('doodle');
		}

		return mods;
	}, [
		includeEmoji,
		includeHashtag,
		includeLink,
		includeMention,
		includeAmount,
		includePrivacy,
		includeGif,
		includeVideoRecording,
		includeAudioRecording,
		includeAttachment,
		includeRecipient,
		includeSuggestionHighlight,
		includeDoodle,
		company.disable_gifs,
		company.bonus_privacy,
		canRecordVideo,
		canRecordAudio,
		canUploadVideo,
		isImageEnabled,
	]);
};
